"use client";

import React from "react";
import { CircleCheck, Wrench, ArrowRight, Sparkles, Calendar, Users, Mail, Phone, Globe, Pencil } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  title: "STI (China) Group",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651741/img/360albumviewer_imgproc_120086703.png",
  editableImage: false,
  introParts: [{
    title: "Division Overview",
    content: "As the core engine of the group in the Greater China region, STI (China) Group focuses on deep incubation and resource integration of premium projects, connecting mature business models with local market precision."
  }, {
    title: "Core Business",
    content: "We are not merely capital providers, but builders of industrial ecosystems, committed to cultivating internationally competitive leading enterprises in technology, consumer goods, and TMT sectors."
  }],
  coreCapabilities: ["Cross‑border project incubation: select and support high‑potential technology, consumer, and TMT projects, providing capital, mentors, and industry resources from 0 to 1.", "Transnational resource integration: relying on the global network of the Singapore headquarters to accurately connect international brands, technology, and capital with Chinese market demand.", "Bilateral trade and investment support: providing cross-border M&A, joint venture establishment, policy consulting, and supply chain connection services for China-Singapore enterprises.", "Regional strategic partner management: managing key partners in the Greater China region, including government agencies, industrial parks, universities, and investment institutions."],
  milestones: [{
    date: "September 2025",
    detail: "STI (China) Group was established in Shenzhen, simultaneously launching the first batch of 5 cross-border incubation projects."
  }, {
    date: "September 2025",
    detail: "Assisted the Singapore Group in establishing the SCI International Research Institute (unveiled in Longgang, Shenzhen), with three research centers, becoming the group's 'Product Manager Trainee' base."
  }, {
    date: "November 2025",
    detail: "Assisted the group's financing target management social platform XiaoMu Biu in completing tens of millions of yuan in angel round financing and pushing its users to exceed 100,000."
  }, {
    date: "January 2026",
    detail: "Successfully built the 'Era Mirror' multi-device parallel shooting platform through Singapore's main resources and technical support, featuring AI directing and AI video generation for efficient AI live broadcasting and recording deployment."
  }, {
    date: "April 2026",
    detail: "Successfully acquired the Italian haute couture suit brand Genesis Source; the branch is currently conducting brand building and 3D scanning technology development for the brand in the Chinese market."
  }],
  team: {
    members: [{
      name: "Peng Junwei",
      role: "President of China Region (Responsible for overall strategy and operations)"
    }, {
      name: "Core Team",
      role: "Composite background in cross-border investment banking, industrial incubation, and brand management, with an average of over 10 years of experience."
    }]
  },
  contactDetails: {
    email: "rickychen@stigroup.cn",
    phone: "+86 021-XXXX-XXXX",
    website: "www.stigroup.cn",
    address: "Building 5A, Zhenye City Mansion, No. 100-1 Puan Road, Liuyue North Community, Henggang Street, Longgang District, Shenzhen"
  },
  coreBusinessItems: ["Integration and connection of transnational resource networks", "Deep incubation of high-quality local projects", "China-Singapore bilateral trade and investment support", "Regional strategic partner management"],
  equipment: ["Full life-cycle project management system", "Cross-border capital flow compliance system", "Multi-dimensional market access database"]
}, {
  title: "SCI International Research Institute",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651741/img/4.png",
  description: "Full Chinese name: 中新创新国际研究院\nEnglish Name: Singapore China International Research Institute\nInstitution Type: Non-profit international scientific research and think tank institution",
  introParts: [{
    title: "Division Overview",
    content: "An internationally leading think tank co-built with Singapore, dedicated to cutting-edge industry research and providing comprehensive strategic support, technological empowerment, and global vision to high-growth enterprises."
  }],
  coreCapabilities: ["Professional Research Center", "Financial Investment Research Center", "Industry Research Center"],
  researchCenterDetails: ["Professional Research Center: Humanities and social sciences, political economy, and new business studies, conducting top-level international professional research and publication.", "Financial Investment Research Center: Backed by Singapore STI Capital, focusing on financial investment research, deeply tied with primary equity, secondary stocks, and professional investment funds.", "Industry Research Center: Based on Singapore STI Group and China Era Innovation Group, co-organized by the institute and enterprises. Conducts deep innovation research on TMT, new consumption, luxury goods, automobiles, and other industries, serving as the STI Group 'Product Manager Trainee' training base."],
  team: {
    members: [{
      name: "John K.-H. Quah",
      role: "President"
    }, {
      name: "Peng Junwei",
      role: "Executive Director"
    }, {
      name: "Liu Junning",
      role: "Professional Research Center Director"
    }, {
      name: "Hu Binyu",
      role: "Financial Investment Research Center Director"
    }, {
      name: "Chen Jie",
      role: "Industry Research Center Director"
    }]
  },
  contactDetails: {
    email: "rickychen@stigroup.cn",
    website: "www.stigroup.cn",
    address: "Building 5A, Zhenye City Mansion, No. 100-1 Puan Road, Liuyue North Community, Henggang Street, Longgang District, Shenzhen"
  },
  coreBusinessItems: ["In-depth research on frontier industrial trends", "International strategic consulting and planning", "Support for transformation of technological innovation achievements", "China-Singapore academic and scientific research exchange platform"],
  equipment: ["Global patent and technology monitoring system", "AI-driven industrial analysis model", "International expert think tank collaboration network"]
}, {
  title: "水贝环球珠宝",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651741/img/1212.png",
  description: "Full legal entity name: 水贝环球（深圳）珠宝有限公司\nEnglish Name: Shuibei Global Jewelry",
  introParts: [{
    title: "Brand Overview",
    content: "Shuibei Global Jewelry is the brand holder of the renowned Chinese jewelry brand. It constructs a full-chain jewelry industry ecosystem covering online e-commerce, supply chains, and offline stores, with ownership of large-chain brands and operation of the jewelry industry vertical platform."
  }],
  coreBusinessItems: ["High-end Brand Strategic Investment: Focusing on high-end consumption fields such as jewelry and gold, empowering brand growth with capital, and promoting omni-channel construction and value enhancement of high-end jewelry brands.", "Digital Upgrade of Luxury Supply Chain: Integrating global jewelry supply chain resources, optimizing procurement, processing, quality inspection, and logistics processes with digital technology to achieve supply chain transparency and efficient collaboration.", "High-Net-Worth Client Asset Allocation Services: Providing professional allocation advice and private customization services for high-end jewelry assets including gold, diamonds, and gemstones for high-net-worth individuals and families.", "International Brand Channel Expansion: Relying on the global resource advantages of the Shuibei jewelry industry cluster to help Chinese jewelry brands enter the international market, while introducing international high-end jewelry brands into the Chinese market to build a two-way circulation channel."],
  brandLayout: "Brand Offline Layout: Shuibei Global Jewelry adopts a dual-wheel drive model of 'online platform + offline chain':\n\nOffline Chain Brand Stores\nOwns large-scale chain brand stores such as Shuibei Global Jewelry and Shuibei Global Gold Bank, covering multiple cities including Shenzhen and Jiangxi, with stores located in core commercial centers.\n\nOnline Platform\nIndependently operates Shuibei Global Network, providing one-stop services such as jewelry information, online selection, and industry matching.",
  contactDetails: {
    address: "Room 5305, Unit 1, Building 1, Jingji Shuibei City Plaza, No. 2090 Wenjin North Road, Shuibei Community, Cuizhu Street, Luohu District, Shenzhen"
  },
  equipment: ["Blockchain anti-counterfeiting and traceability technology", "Luxury big data analysis platform", "Global high-end retail management system"]
}, {
  title: "九维资本",
  image: "https://casual-sponge.10web.cloud/wp-content/uploads/2026/05/九维资本logo.png",
  description: "",
  subDescription: "Full legal entity name: 深圳市九维创新资本管理有限公司\nEnglish Name: Jiuwei Innovation Capital\nFunds under management: Shenzhen Jiuwei Innovation and Entrepreneurship Investment Enterprise (Limited Partnership), etc.",
  introParts: [{
    title: "Division Overview",
    content: "Jiuwei Innovation Capital is a professional equity investment platform under Jiuwei Innovation Capital Management (Shenzhen) Co., Ltd. As the financial hub of the group's capital + industry + innovation strategy, it focuses on equity investments in internet, TMT, and new consumption sectors across all VC and PE stages."
  }, {
    title: "Core Investment Areas",
    content: "Internet Sector\nFocusing on internet applications and infrastructure with platform attributes, user stickiness, and growth potential, covering social, tools, SaaS, and other directions.\n\nTMT Sector\nDeeply cultivating technology, media, and communication industries, focusing on innovative enterprises driven by frontier technologies such as artificial intelligence, big data, and industrial digitalization.\n\nNew Consumption Sector\nCapturing opportunities in consumption upgrading and brand rise, investment directions cover new brands, new channels, and new experiences, helping domestic brands go global."
  }],
  coreBusinessItems: ["Private equity fund initiation and management", "Corporate M&A and restructuring advisory", "Risk assessment and post-investment management", "Family office customized services"],
  equipment: ["Real-time global financial monitoring terminal", "Quantitative risk control evaluation model", "Intelligent investment research decision support system"],
  contactDetails: {
    address: "Rooms 1302-1304, Central Business Building, No. 88 Fuhua 1st Road, Futian District, Shenzhen"
  },
  imageClassName: "absolute inset-0 bg-primary/10 mix-blend-multiply z-10"
}, {
  title: "秀时代/时代觉醒",
  image: "https://stiholdinggroup.com/wp-content/uploads/2026/05/1212.png",
  editableImage: false,
  introParts: [{
    title: "Brand Overview",
    content: "STI Group's full-industry-chain media ecosystem platform and digital content & brand communications leader. With deep expertise in media, it owns China's largest indoor automotive film & television production base, engaged in comprehensive 4A advertising services, film, television, and variety shows production, and network content production. Its MCN matrix accounts for tens of millions of total followers with over 1 billion+ network communications data."
  }, {
    title: "Entity Information",
    content: "Shenzhen Show Era Culture Media Co., Ltd.\nShenzhen Era Awakening Culture Technology Co., Ltd."
  }, {
    title: "Core Institution I",
    content: "Show Era Culture Media: Focusing on film and television production, 4A advertising full-service, and large-scale variety show production."
  }, {
    title: "Core Institution II",
    content: "Era Awakening Technology: Focusing on MCN matrix operation, short video incubation, and digital marketing."
  }, {
    title: "Core Business",
    content: "1. Film and Television Content Production: Covering movies, TV series, and top-tier variety show production.\n2. MCN Matrix Operation: Managing a matrix of tens of millions of followers to achieve efficient traffic conversion.\n3. 4A Advertising Full-Service Agency: Providing brand strategy, creative design, and full-media placement.\n4. Film and Television Production and Investment: Relying on the largest indoor base in China to provide professional shooting environments."
  }, {
    title: "Core Data",
    content: "Total MCN Followers: Tens of millions\nNetwork-wide Communication Data: 1 billion+\nBase Scale: Leading indoor automotive film and television base in China"
  }],
  coreBusinessItems: ["Film and television content production", "MCN matrix operation", "4A advertising full-service agency", "Film and television production and investment"],
  contactDetails: {
    address: "3F 301, International Automobile Exhibition Center, No. 98 Longfei Avenue, Yuanshan Street, Longgang District, Shenzhen"
  },
  description: "",
  editableImageClassName: ""
}, {
  title: "小目Biu",
  company_full_name: "Era Shine (Shenzhen) Technology Co., Ltd.",
  strategic_positioning: "Socialized Goal Management Platform · Making Self-discipline a Fun Social Game",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651741/img/11222.png",
  introParts: [{
    title: "Division Overview",
    content: "XiaoMu Biu is a social goal-management platform that transforms self-discipline into an engaging social game, launched on November 1, 2025. Core concept: People with goals can chat together."
  }, {
    title: "Core Business",
    content: "XiaoMu Biu supports multi-scenario applications: personal self-discipline challenges, couple interaction challenges, team collaboration challenges. It adopts a dual identity system of Challenger and Witness, building a mutually supportive community."
  }],
  coreBusinessItems: ["Multi-dimensional Goal Creation - Supporting goal settings in categories like study, fitness, work, and life, with built-in challenge deposit mechanisms", "Social Supervision System - Inviting friends as witnesses to build a mutually supportive social community", "Basic Flag Mode - Classic goal marking and completion tracking mode", "PK Battle Mode - Goal PK competitions with other users", "Collaborative Challenge Mode - Collaborative challenges involving team members", "Virtual PK Mode - Intelligent battles with AI or virtual characters", "Smart Reminders and Community Interaction - Personalized goal reminders and community sharing functions"],
  contactDetails: {
    website: "www.xiaomubiu.cn",
    address: "301-10, Building 1, Shenzhen Software Park, No. 2 Gaoxin Middle 2nd Road, Maling Community, Yuehai Street, Nanshan District, Shenzhen"
  },
  equipment: ["Enterprise Marketing Tools - Goal-challenge-based brand solutions, supporting customized marketing activities, providing interactive marketing scenarios for B-end users"]
}];
function ServiceCard({
  service,
  index
}: {
  service: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
      <Card className={`overflow-hidden flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 border-border/40 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-card rounded-lg`}>
        
        <div className={`${service.editableImageClassName || ""} w-full lg:w-1/2 relative aspect-video lg:aspect-auto group/img`}>
          <div className={`${service.imageClassName || ""} absolute inset-0 bg-primary/10 mix-blend-multiply z-10`} />
          <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-wvc-srcset={`${service.image}-wvc-srcset`} data-wvc-sizes={`${service.image}-wvc-sizes`} />
          {service.editableImage && <div className="absolute inset-0 z-20 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
              <div className="bg-white/20 backdrop-blur-md p-3 rounded-lg border border-white/30">
                <Pencil className="w-6 h-6 text-white" />
              </div>
            </div>}
        </div>

        <div className="w-full lg:w-1/2 p-8 sm:p-10 lg:p-14 flex flex-col">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent text-accent-foreground text-sm font-default font-medium w-fit mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="tracking-wider uppercase">Core Business Divisions</span>
          </div>
          
          <h3 className="text-3xl sm:text-4xl font-default font-bold text-foreground mb-4 tracking-tight">
            {service.title}
          </h3>
          
          <div className="max-h-[400px] overflow-y-auto pr-4">
            {service.introParts && service.introParts.length > 0 ? <div className="mb-8">
                {service.introParts.filter((part: any) => index !== 6 || part.content !== "深圳市龙岗区横岗街道六约北社区普安路100-1号振业城公馆5栋A").map((part: any, i: number) => <div key={i} data-index={i} className="mb-6 last:mb-0">
                    <p className={`text-muted-foreground text-lg leading-relaxed font-default whitespace-pre-line ${index === 4 || index === 5 ? 'font-normal' : ''}`}>
                      {part.content}
                    </p>
                  </div>)}
                {service.description && <div className="mt-4 border-l-2 border-primary/20 pl-4">
                    <p className="text-muted-foreground text-sm leading-relaxed font-default whitespace-pre-line">
                      {service.description}
                    </p>
                    {service.subDescription && <span className="block mt-2 text-muted-foreground text-[11px] leading-relaxed font-default whitespace-pre-line opacity-80">
                        {service.subDescription}
                      </span>}
                  </div>}
              </div> : <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-default whitespace-pre-line">
                {service.description}
              </p>}

            {(service.introParts?.some((p: any) => p.title === "品牌简介") || index === 5) && service.coreBusinessItems?.length > 0 && <div className="mb-10">
                
                <ul className="space-y-3">
                  {service.coreBusinessItems.map((item: string, j: number) => <li key={j} data-index={j} className="flex items-start gap-2.5 text-sm text-muted-foreground font-default">
                      {index === 2 ? <CircleCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" /> : <span className="w-1.5 h-1.5 rounded-lg bg-primary mt-1.5 shrink-0" />}
                      <span className="leading-snug">{item}</span>
                    </li>)}
                </ul>
              </div>}

            {index === 6 && <p className="text-muted-foreground text-lg leading-relaxed font-default whitespace-pre-line font-normal mb-8">深圳市龙岗区横岗街道六约北社区普安路100-1号振业城公馆5栋A</p>}

            {service.brandLayout && <div className="mb-8">
                <h4 className="text-lg font-bold text-foreground mb-2">Brand Layout</h4>
                <p className="text-muted-foreground text-sm leading-relaxed font-default whitespace-pre-line">
                  {service.brandLayout}
                </p>
              </div>}

            {service.coreCapabilities && <div className="mb-8">
                
                <ul className="grid sm:grid-cols-2 gap-3">
                  {service.coreCapabilities.map((cap: string, i: number) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground font-default">
                      <CircleCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="leading-snug">{cap}</span>
                    </li>)}
                </ul>
              </div>}

            {service.researchCenterDetails && <div className="mb-8">
                
                {service.researchCenterDetails.map((detail: string, i: number) => <p key={i} data-index={i} className="text-sm text-muted-foreground mb-4 last:mb-0 leading-relaxed">
                    {detail}
                  </p>)}
              </div>}

            {service.milestones && <div className="mb-8">
                <div className="space-y-4">
                  {service.milestones.map((m: any, i: number) => <div key={i} data-index={i} className="border-l-2 border-primary/20 pl-4 py-1">
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">{m.date}</span>
                      <p className="text-sm text-muted-foreground mt-1">{m.detail}</p>
                    </div>)}
                </div>
              </div>}

            {service.team && <div className="mb-8">
                
                <div className="flex flex-wrap gap-4">
                  {service.team.members.map((member: any, i: number) => <div key={i} data-index={i} className="bg-muted/50 px-4 py-2 rounded-lg">
                      <p className="text-sm font-bold text-foreground">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.role}</p>
                    </div>)}
                </div>
              </div>}

            {service.contactDetails && <div className="mb-8 pt-6 border-t border-border/40">
                <h4 className="text-lg font-bold text-foreground mb-4">{index === 2 || index === 4 ? "Headquarters Address" : index === 3 ? "Office Address" : "Contact Us"}</h4>
                <div className="grid gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.contactDetails.email && <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4 text-primary" />
                      <span>{service.contactDetails.email}</span>
                    </div>}
                    
                    {service.contactDetails.website && <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Globe className="w-4 h-4 text-primary" />
                      <span>{service.contactDetails.website}</span>
                    </div>}
                  </div>
                  {service.contactDetails.address && <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <div className="w-4 h-4 text-primary mt-0.5 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                    </div>
                    <span>{service.contactDetails.address}</span>
                  </div>}
                </div>
              </div>}
          </div>

          <div className="mt-auto pt-8">
            <Button asChild size="lg" className="w-full sm:w-auto group rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
              
            </Button>
          </div>
        </div>

      </Card>
      
    </div>;
}
export default function CatalogoServizi() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="228958" id="集团旗下子公司" className="py-24 lg:py-32 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent/10 via-background to-background -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight uppercase">
            Subsidiaries & Portfolio Companies
          </h2>
          <p className="text-xl text-muted-foreground font-default leading-relaxed">
            Through diversified industry positioning, STI Group has established a global investment ecosystem covering finance, technology, media, and luxury goods, creating sustained value for our partners.
          </p>
        </div>

        <div className="flex flex-col gap-12 lg:gap-20">
          {servicesData.map((service, index) => <ServiceCard key={index} service={service} index={index} />)}
        </div>

      </div>
    </section>;
}